// visitWire.ts - the wire contract between the browser's analytics client
// (src/visits/) and the Overwatch relay (server/analytics.ts).
//
// The relay drops any event name not in OW_EVENTS and any parameter not of
// the kind declared here, so a client event typed against OwEventParams is
// exactly what survives the relay.

export const RELAY_VISIT_PATH = '/a/v';
export const RELAY_EVENTS_PATH = '/a/e';

/** A POST /a/e over either limit is refused whole. */
export const RELAY_MAX_EVENTS = 20;
export const RELAY_MAX_BYTES = 8 * 1024;

/** A visit token is good for this long after GET /a/v minted it. */
export const VISIT_TOKEN_TTL_S = 12 * 60 * 60;

/** Overwatch's own sentinel for a visitor with no device id. */
export const ANONYMOUS_USER_ID = 'overwatch:anonymous';

/** `4xciv-` plus 16 random bytes as lowercase hex; anything else is sent as anonymous. */
export const DEVICE_ID_PATTERN = /^4xciv-[0-9a-f]{32}$/;

/** A registry id, a site slug or a short build hash. Free text never matches. */
export const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

export type RelayRegion = 'eu' | 'row';
export type AnalyticsSite = 'explore' | 'play';

/** GET /a/v answers this. `eu` covers the EEA, the UK and Switzerland. */
export interface VisitResponse {
  region: RelayRegion;
  token: string;
}

export type ParamSpec =
  | { readonly kind: 'id' }
  | { readonly kind: 'enum'; readonly values: readonly string[] }
  | { readonly kind: 'number'; readonly min: number; readonly max: number; readonly integer?: boolean }
  | { readonly kind: 'boolean' };

/** Whether the relay keeps a parameter value of this kind. */
export function paramAccepts(spec: ParamSpec, value: unknown): value is string | number | boolean {
  switch (spec.kind) {
    case 'id':
      return typeof value === 'string' && ID_PATTERN.test(value);
    case 'enum':
      return typeof value === 'string' && spec.values.includes(value);
    case 'number':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= spec.min &&
        value <= spec.max &&
        (!spec.integer || Number.isInteger(value))
      );
    case 'boolean':
      return typeof value === 'boolean';
  }
}

const id = { kind: 'id' } as const;
const count = { kind: 'number', min: 0, max: 100_000, integer: true } as const;
const ms = { kind: 'number', min: 0, max: 3_600_000 } as const;
const seconds = { kind: 'number', min: 0, max: 7 * 24 * 3600 } as const;
const days = { kind: 'number', min: 0, max: 1_000_000 } as const;
const entryView = { kind: 'enum', values: ['solar_system', 'cruise', 'atlas_body'] } as const;
/** The deepest Atlas band a visit reached; `none` for a visit that never entered one. */
const deepestBand = {
  kind: 'enum',
  values: ['none', 'SYSTEM', 'ORBIT', 'REGIONAL', 'LOCAL', 'CLAIM', 'PLOT', 'SURFACE'],
} as const;

/** Every event may carry these; `site` is not among them, the relay sets it from the distribution. */
export const OW_COMMON_PARAMS = { build: id } as const satisfies Record<string, ParamSpec>;

/**
 * The Overwatch half of the event catalogue: name to parameter kinds.
 * `site_slug` is walkaround_start's landing site, renamed from the spec's
 * `site` because every event's `site` already names explore or play.
 */
export const OW_EVENTS = {
  visit: { entry_view: entryView },
  app_ready: {
    entry_view: entryView,
    load_ms: ms,
    webgl: { kind: 'number', min: 1, max: 2, integer: true },
    input: { kind: 'enum', values: ['touch', 'mouse'] },
    screen: { kind: 'enum', values: ['s', 'm', 'l'] },
  },
  walkaround_start: { body_id: id, site_slug: id, build_ms: ms },
  rover_drive: { body_id: id, odometer_m: { kind: 'number', min: 0, max: 10_000_000 }, seconds },
  trip_start: { origin_id: id, target_id: id, dv_kms: { kind: 'number', min: 0, max: 1000 }, tof_days: days },
  trip_end: {
    target_id: id,
    outcome: { kind: 'enum', values: ['arrived', 'handback', 'refused', 'cancelled'] },
    sim_days: days,
    real_s: seconds,
    skips: count,
  },
  land_start: { body_id: id },
  visit_end: {
    s_solar: seconds,
    s_cruise: seconds,
    s_land: seconds,
    s_walk: seconds,
    deepest_band: deepestBand,
    bodies: count,
    trips: count,
    errors: count,
    p95_ms: ms,
  },
} as const satisfies Record<string, Record<string, ParamSpec>>;

export type OwEventName = keyof typeof OW_EVENTS;

type ParamValue<S> = S extends { kind: 'enum'; values: readonly (infer V)[] }
  ? V
  : S extends { kind: 'number' }
    ? number
    : S extends { kind: 'boolean' }
      ? boolean
      : string;

type ParamsOf<T> = { [K in keyof T]?: ParamValue<T[K]> };

export type OwEventParams<N extends OwEventName> = ParamsOf<(typeof OW_EVENTS)[N]> & ParamsOf<typeof OW_COMMON_PARAMS>;

export interface RelayUtm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}

/**
 * One event on the wire. `referrer` is an absolute http(s) URL cut to origin
 * and path; it and `utm` belong on `visit`, and the relay passes them on any
 * event it accepts.
 */
export type RelayEvent = {
  [N in OwEventName]: { name: N; params?: OwEventParams<N>; referrer?: string; utm?: RelayUtm };
}[OwEventName];

/** The body of POST /a/e, JSON, at most RELAY_MAX_EVENTS events and RELAY_MAX_BYTES bytes. */
export interface RelayBatch {
  token: string;
  /** A DEVICE_ID_PATTERN id for a counted visitor; omitted, the relay sends ANONYMOUS_USER_ID. */
  userId?: string;
  events: RelayEvent[];
}

/** What POST /a/e answers with a 202. */
export interface RelayAccepted {
  accepted: number;
  dropped: number;
}
